import { useEffect, useState } from 'react';
import { api, isApiError } from '@/lib/api';
import { logger } from '@/lib/logger';
import type { Locale } from '@/lib/i18n';

export interface MarketplaceProductCard {
  productId: string;
  name: string;
  image?: string | null;
  categoryName?: string | null;
  gameName?: string | null;
  gameSlug?: string | null;
  listingType?: string | null;
  price: string;
  currency: string;
  offerCount: number;
  sellerName: string;
  sellerUsername: string | null;
  sellerAvatarUrl?: string | null;
  sellerRating?: number | null;
  sellerRatingCount?: number;
  sellerVerified?: boolean;
  sellerCompletedOrders?: number;
  sellerRank?: string | null;
  sellerOnline?: boolean;
  unitsSold?: number;
  deliveryMinutes: number | null;
  deliveryMethod?: string | null;
  warrantyDays: number | null;
  createdAt: string;
}

export interface MarketplaceCategory {
  id: string;
  name: string;
  slug: string;
  category: string;
  logo: string | null;
  liveCount: number;
}

export interface MarketplaceStats {
  ordersCompleted: number | null;
  activeSellers: number | null;
  avgDeliveryMinutes: number | null;
}

export interface MarketplaceHomePayload {
  featured: MarketplaceProductCard[];
  recent: MarketplaceProductCard[];
  categories: MarketplaceCategory[];
  stats: MarketplaceStats;
}

export interface MarketplaceHomeState {
  data: MarketplaceHomePayload | null;
  loading: boolean;
}

export function useMarketplaceHome(): MarketplaceHomeState {
  const [data, setData] = useState<MarketplaceHomePayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get<MarketplaceHomePayload>('/marketplace/home')
      .then((res) => {
        if (cancelled) return;
        if (isApiError(res)) {
          logger.warn('marketplace/home request failed', { code: res.error.code });
        } else {
          setData(res.data);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading };
}

export function formatListingPrice(
  price: string | number,
  currency: string,
  locale: Locale,
): string {
  const amount = typeof price === 'string' ? Number(price) : price;
  if (!Number.isFinite(amount)) return String(price);
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  } catch {
    return `${new Intl.NumberFormat(locale).format(amount)} ${currency}`;
  }
}

export interface PluralForms {
  one: string;
  other: string;
  [form: string]: string;
}

export function pluralize(forms: PluralForms, count: number, locale: Locale): string {
  const rule = new Intl.PluralRules(locale).select(count);
  return forms[rule] ?? forms.other;
}

export function formatCount(count: number, locale: Locale): string {
  return new Intl.NumberFormat(locale).format(count);
}

export function formatMinutes(minutes: number, forms: PluralForms, locale: Locale): string {
  const rounded = Math.round(minutes);
  return `${formatCount(rounded, locale)} ${pluralize(forms, rounded, locale)}`;
}
